import { useEffect } from "react";
import { useSelector } from "react-redux";
import { store } from "..";
import { listenForGlobal } from "../firebase/global";
import { ApplicationState } from "../reducers";
import { listenForObservations } from "./firestore/observations";
import { getOrganisationInfo } from "./firestore/organisationInfo";
import { listenForScouts } from "./firestore/scouts";
import { listenForGunshots } from "./firestore/triangulations";
import { getUserForOrganisation } from "./firestore/userForOrganisation";
import { addOrganisationInfoAction, cleanupOrganisationReducer } from "./organisationReducer";

/**
 * Feeds the organisation reducer with everything the organisation and device pages show:
 * organisation info, scouts, observations, gunshots, fast-track enrolments and the organisation user's JWT.
 * Unsubscribes and clears the info when the page leaves or the organisation changes.
 */
export const useOrganisationListeners = (organisationId: string, jwtToken: string | undefined) => {
  const userJwtToken = useSelector((state: ApplicationState) => state.organisation.userJwtToken);

  useEffect(() => {
    if (!jwtToken) {
      return undefined;
    }
    store.dispatch(cleanupOrganisationReducer());
    getOrganisationInfo(organisationId);
    const unsubscribers = [listenForScouts(organisationId), listenForObservations(organisationId), listenForGunshots(organisationId)];
    getUserForOrganisation(organisationId, jwtToken);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      store.dispatch(addOrganisationInfoAction(undefined));
    };
  }, [organisationId, jwtToken]);

  // The fast-track list is scoped to the organisation in the JWT that asks for it, so it has to be fetched
  // with the organisation user's JWT, which arrives after the lookup above; the admin's own JWT would
  // return the admin's organisation instead.
  useEffect(() => {
    if (!userJwtToken) {
      return undefined;
    }
    return listenForGlobal(userJwtToken);
  }, [userJwtToken]);
};
